import {
  Box,
  Button,
  Flex,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Text,
  useDisclosure
} from '@chakra-ui/react'
import React, { useCallback, useEffect, useState } from 'react'
import {
  FaArrowLeft,
  FaArrowRight,
  FaChevronDown,
  FaSearch,
  FaTimes
} from 'react-icons/fa'
import { LABEL } from '../../../../constants'
import { getAllArticleRules } from '../../../ArticleRulesPage/api/getAllArticleRules'

const PaginatedDropdown = ({
  value,
  onChange,
  fetchData,
  label = LABEL.GENERIC_DROPDOWN.label,
  placeholder = LABEL.GENERIC_DROPDOWN.placeholder,
  size = 10,
  data = false,
  cifValue,
  isPartner = false,
  expenseGroup,
  attribute,
  nullDefault,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [items, setItems] = useState([])
  const [isLoading, setIsLoading] = useState(false)

  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedItemName, setSelectedItemName] = useState(nullDefault ? null : '')

  const loadItems = useCallback(async () => {
    if (cifValue !== undefined && !cifValue) {
      setItems([])
      setTotalPages(0)
      setIsLoading(false)
      return
    }

    if (expenseGroup) {
      try {
        setIsLoading(true)
        const response = await getAllArticleRules({ expenseGroup })
        const groups = response.content || []
        const group = groups.find(
          g => g.worldClassExpenseGroup === expenseGroup
        )

        if (group) {
          const filtered = group.articles.filter(a =>
            a.name.toLowerCase().includes(searchTerm.toLowerCase())
          )
          const start = currentPage * size
          const pageItems = filtered.slice(start, start + size)

          setItems(pageItems)
          setTotalPages(Math.ceil(filtered.length / size))
          return
        }
      } catch (error) {
        console.error('Error loading articles', error)
      } finally {
        setIsLoading(false)
      }
      return
    }

    try {
      setIsLoading(true)
      const response = await fetchData({
        cif: cifValue,
        searchTerm,
        pageNumber: currentPage,
        size,
        orderByList: [
          {
            field: 'name',
            direction: 'ASCENDING'
          }
        ]
      })

      if (response) {
        let totalItems = response.total
        let content = response.content
        if (data) {
          totalItems = response.data.total
          content = response.data.content
        }
        setItems(content || [])
        setTotalPages(Math.ceil(totalItems / size))
      }
    } catch (error) {
      console.error('Error loading dropdown data', error)
    } finally {
      setIsLoading(false)
    }
  }, [fetchData, currentPage, searchTerm, size, cifValue, expenseGroup, data])

  useEffect(() => {
    if (!isOpen) return
    loadItems()
  }, [isOpen, loadItems])

  useEffect(() => {
    setCurrentPage(0)
  }, [searchTerm])

  const handleSelectItem = item => {
    setSelectedItemName(item)
    switch (attribute) {
      case 'cif':
        onChange(item.cif)
        break
      case 'id':
        onChange(item.id)
        break
      default:
        onChange(item.name)
    }
  }

  const handleClearSelection = e => {
    e.stopPropagation()
    if(nullDefault) {
      setSelectedItemName(null)
      onChange(null)
    }
    else {
      setSelectedItemName('')
      onChange('')
    }
    
  }

  const handlePrevious = () => {
    if (currentPage > 0) {
      setCurrentPage(previous => previous - 1)
    }
  }

  const handleNext = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(previous => previous + 1)
    }
  }

  const handleSearchChange = event => {
    setSearchTerm(event.target.value)
    setCurrentPage(0)
  }

  return (
    <Menu
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      closeOnSelect
      isLazy
      lazyBehavior="unmount"
      portalProps={{ appendToParentPortal: true }}
    >
      <Flex align="center" width="100%">
        <MenuButton
          as={Button}
          variant="outline"
          textColor="black"
          fontSize="sm"
          size="sm"
          width="100%"
          textAlign="left"
          fontWeight="light"
          rightIcon={<FaChevronDown />}
          border="1px"
          borderColor="black"
        >
          {isPartner
            ? selectedItemName
              ? selectedItemName.name || placeholder
              : value || placeholder
            : value || placeholder}
        </MenuButton>
        {value && (
          <IconButton
            icon={<FaTimes />}
            size="sm"
            ml={2}
            aria-label={LABEL.CLEAR_SELECTION.label}
            onClick={handleClearSelection}
          />
        )}
      </Flex>

      <MenuList width="600px" p="2">
        <Box display="flex" alignItems="center" mb="2">
          <FaSearch style={{ marginRight: '6px', color: '#718096' }} />
          <Input
            placeholder={LABEL.SEARCH.label}
            size="sm"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </Box>

        {isLoading ? (
          <Box textAlign="center" p="2" width="600px" height="400px">
            <Spinner size="sm" />
          </Box>
        ) : (
          <>
            {items.length === 0 ? (
              <Box textAlign="center" p="2">
                <Text fontSize="sm" color="gray.500">
                  {LABEL.EMPTY_FILTER.label}
                </Text>
              </Box>
            ) : (
              items.map(item => (
                <MenuItem
                  key={item.id}
                  onClick={() => handleSelectItem(item)}
                  fontSize="sm"
                >
                  {item.name}
                </MenuItem>
              ))
            )}
          </>
        )}

        <Flex
          mt="2"
          justify="space-between"
          align="center"
          px="2"
          visibility={isLoading ? 'hidden' : 'visible'}
        >
          <IconButton
            icon={<FaArrowLeft />}
            size="xs"
            aria-label={LABEL.PREVIOUS_PAGE.label}
            onClick={handlePrevious}
            isDisabled={currentPage <= 0}
          />
          <Text fontSize="xs">
            Page {currentPage + 1} of {Math.max(totalPages, 1)}
          </Text>
          <IconButton
            icon={<FaArrowRight />}
            size="xs"
            aria-label={LABEL.NEXT_PAGE.label}
            onClick={handleNext}
            isDisabled={currentPage >= totalPages - 1 || totalPages === 0}
          />
        </Flex>
      </MenuList>
    </Menu>
  )
}

export default PaginatedDropdown;
